import { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Controls } from 'components/shared/Controls';
import { SettingsPopout } from 'components/shared/SettingsPopout';
import { VolatilitySurface3D as VolatilitySurface3DType } from 'types/moviefone';
import { ProjectedSurface } from 'gen/stream-api/requests';
import {
  useVolSurfaceGrids,
  ExpirationDensity,
  StrikeDensity,
  SurfaceView,
  PruneLevel,
} from './useVolSurfaceGrids';
import VolatilitySurface3D from './VolatilitySurface3D';
import VolatilityHeatmap from './VolatilityHeatmap';

interface VolatilityChartPanelProps {
  surfaceData: VolatilitySurface3DType;
  projectedSurface?: ProjectedSurface | null;
  isFullScreen?: boolean;
}

type ChartType = 'surface' | 'heatmap';

const EXPIRATION_DENSITY_LABELS: Record<ExpirationDensity, string> = {
  fewer: 'Fewer',
  normal: 'Default',
  more: 'More',
};

const STRIKE_DENSITY_LABELS: Record<StrikeDensity, string> = {
  sparse: 'Fewer',
  normal: 'Default',
  dense: 'More',
};

// Same labels as the real Volatility Dashboard's Fixed Strike Matrix prune settings.
const PRUNE_LEVEL_LABELS: Record<PruneLevel, string> = {
  none: 'None',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const HeatmapSettings = ({
  expirationDensity,
  onExpirationDensityChange,
  strikeDensity,
  onStrikeDensityChange,
  pruneExpirations,
  onPruneExpirationsChange,
  pruneStrikes,
  onPruneStrikesChange,
}: {
  expirationDensity: ExpirationDensity;
  onExpirationDensityChange: (value: ExpirationDensity) => void;
  strikeDensity: StrikeDensity;
  onStrikeDensityChange: (value: StrikeDensity) => void;
  pruneExpirations: PruneLevel;
  onPruneExpirationsChange: (value: PruneLevel) => void;
  pruneStrikes: PruneLevel;
  onPruneStrikesChange: (value: PruneLevel) => void;
}) => {
  // Each Select's portalled menu reads as an outside click to SettingsPopout's ClickAwayListener - externalMenuOpen prevents that.
  const [expirationsOpen, setExpirationsOpen] = useState(false);
  const [strikesOpen, setStrikesOpen] = useState(false);
  const [pruneExpirationsOpen, setPruneExpirationsOpen] = useState(false);
  const [pruneStrikesOpen, setPruneStrikesOpen] = useState(false);
  const theme = useTheme();
  const outlineSx = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
  };

  return (
    <SettingsPopout
      title="Heatmap Settings"
      popperID="vol-heatmap-settings"
      externalMenuOpen={
        expirationsOpen ||
        strikesOpen ||
        pruneExpirationsOpen ||
        pruneStrikesOpen
      }
    >
      <Stack sx={{ textAlign: 'left', gap: 4 }}>
        <Typography>Heatmap Settings</Typography>
        {/* Paired 50%-width rows, matching the Fixed Strike Matrix's settings layout/spacing. */}
        <Stack gap={6} width="100%">
          <Stack direction="row" gap={3} width="100%">
            <FormControl sx={{ width: '50%' }} size="small">
              <InputLabel id="heatmap-expirations-label">
                Expirations
              </InputLabel>
              <Select
                labelId="heatmap-expirations-label"
                label="Expirations"
                value={expirationDensity}
                onChange={(e: SelectChangeEvent) =>
                  onExpirationDensityChange(e.target.value as ExpirationDensity)
                }
                open={expirationsOpen}
                onOpen={() => setExpirationsOpen(true)}
                onClose={() => setExpirationsOpen(false)}
                input={<OutlinedInput sx={outlineSx} label="Expirations" />}
                MenuProps={{
                  sx: { zIndex: 10_001 },
                  PaperProps: { style: { maxHeight: '200px' } },
                }}
              >
                {(
                  Object.keys(EXPIRATION_DENSITY_LABELS) as ExpirationDensity[]
                ).map((value) => (
                  <MenuItem key={value} value={value}>
                    {EXPIRATION_DENSITY_LABELS[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: '50%' }} size="small">
              <InputLabel id="heatmap-strikes-label">Strikes</InputLabel>
              <Select
                labelId="heatmap-strikes-label"
                label="Strikes"
                value={strikeDensity}
                onChange={(e: SelectChangeEvent) =>
                  onStrikeDensityChange(e.target.value as StrikeDensity)
                }
                open={strikesOpen}
                onOpen={() => setStrikesOpen(true)}
                onClose={() => setStrikesOpen(false)}
                input={<OutlinedInput sx={outlineSx} label="Strikes" />}
                MenuProps={{
                  sx: { zIndex: 10_001 },
                  PaperProps: { style: { maxHeight: '200px' } },
                }}
              >
                {(Object.keys(STRIKE_DENSITY_LABELS) as StrikeDensity[]).map(
                  (value) => (
                    <MenuItem key={value} value={value}>
                      {STRIKE_DENSITY_LABELS[value]}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" gap={3} width="100%">
            <FormControl sx={{ width: '50%' }} size="small">
              <InputLabel id="heatmap-prune-expirations-label">
                Prune Expirations
              </InputLabel>
              <Select
                labelId="heatmap-prune-expirations-label"
                label="Prune Expirations"
                value={pruneExpirations}
                onChange={(e: SelectChangeEvent) =>
                  onPruneExpirationsChange(e.target.value as PruneLevel)
                }
                open={pruneExpirationsOpen}
                onOpen={() => setPruneExpirationsOpen(true)}
                onClose={() => setPruneExpirationsOpen(false)}
                input={
                  <OutlinedInput sx={outlineSx} label="Prune Expirations" />
                }
                MenuProps={{
                  sx: { zIndex: 10_001 },
                  PaperProps: { style: { maxHeight: '200px' } },
                }}
              >
                {(Object.keys(PRUNE_LEVEL_LABELS) as PruneLevel[]).map(
                  (value) => (
                    <MenuItem key={value} value={value}>
                      {PRUNE_LEVEL_LABELS[value]}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <FormControl sx={{ width: '50%' }} size="small">
              <InputLabel id="heatmap-prune-strikes-label">
                Prune Strikes
              </InputLabel>
              <Select
                labelId="heatmap-prune-strikes-label"
                label="Prune Strikes"
                value={pruneStrikes}
                onChange={(e: SelectChangeEvent) =>
                  onPruneStrikesChange(e.target.value as PruneLevel)
                }
                open={pruneStrikesOpen}
                onOpen={() => setPruneStrikesOpen(true)}
                onClose={() => setPruneStrikesOpen(false)}
                input={<OutlinedInput sx={outlineSx} label="Prune Strikes" />}
                MenuProps={{
                  sx: { zIndex: 10_001 },
                  PaperProps: { style: { maxHeight: '200px' } },
                }}
              >
                {(Object.keys(PRUNE_LEVEL_LABELS) as PruneLevel[]).map(
                  (value) => (
                    <MenuItem key={value} value={value}>
                      {PRUNE_LEVEL_LABELS[value]}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Stack>
    </SettingsPopout>
  );
};

const VolatilityChartPanel = ({
  surfaceData,
  projectedSurface,
  isFullScreen = false,
}: VolatilityChartPanelProps) => {
  const [chartType, setChartType] = useState<ChartType>('surface');
  const [view, setView] = useState<SurfaceView>('current');
  const [expirationDensity, setExpirationDensity] =
    useState<ExpirationDensity>('normal');
  const [strikeDensity, setStrikeDensity] = useState<StrikeDensity>('normal');
  const [pruneExpirations, setPruneExpirations] = useState<PruneLevel>('none');
  const [pruneStrikes, setPruneStrikes] = useState<PruneLevel>('none');

  const surfaces = useVolSurfaceGrids(
    surfaceData,
    projectedSurface,
    strikeDensity,
    expirationDensity,
    pruneExpirations,
    pruneStrikes,
    chartType === 'heatmap' ? 'listed' : 'even',
  );

  if (surfaces == null) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No volatility surface data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: isFullScreen ? '70vh' : 500,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Two separate Controls calls, not one with both button groups, so
          the gap between them can be widened here without changing the
          shared Controls component's own (smaller) inter-group spacing,
          which other pages (e.g. Indices) also rely on. */}
      <Stack
        direction="row"
        sx={{ gap: 4, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <Controls
          buttonGroups={[
            {
              buttons: [
                { value: 'surface', label: '3D Surface' },
                { value: 'heatmap', label: 'Heatmap' },
              ],
              setter: setChartType,
              curValue: chartType,
            },
          ]}
        />
        <Controls
          buttonGroups={[
            {
              buttons: [
                { value: 'current', label: 'Current IVs' },
                { value: 'projected', label: 'Projected IVs' },
                { value: 'gap', label: 'IV Gap' },
              ],
              setter: setView,
              curValue: view,
            },
          ]}
        />
        {/* Always rendered so the pills next to it never shift on toggle. */}
        <Box
          sx={{ visibility: chartType === 'heatmap' ? 'visible' : 'hidden' }}
        >
          <HeatmapSettings
            expirationDensity={expirationDensity}
            onExpirationDensityChange={setExpirationDensity}
            strikeDensity={strikeDensity}
            onStrikeDensityChange={setStrikeDensity}
            pruneExpirations={pruneExpirations}
            onPruneExpirationsChange={setPruneExpirations}
            pruneStrikes={pruneStrikes}
            onPruneStrikesChange={setPruneStrikes}
          />
        </Box>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {chartType === 'surface' ? (
          <VolatilitySurface3D surfaces={surfaces} view={view} />
        ) : (
          <VolatilityHeatmap surfaces={surfaces} view={view} />
        )}
      </Box>
    </Box>
  );
};

export default VolatilityChartPanel;
